// country-language.js
import { loadColumns } from './addressSearch.js';
import { loadDynamicBaseLayers } from './loadBaseLayers.js';
import { API_URL } from './config.js';
import { decodeJwtClaims, getAccessToken, secureFetch } from "./securityUtil.js";
import { loadLanguageTranslations } from './languageTranslator.js';


let onCountryLanguageChange = null;
const selectorStateByMap = new WeakMap();

function getValueByKeys(item, keys = []) {
  for (const key of keys) {
    if (item?.[key] !== undefined && item[key] !== null) {
      return item[key];
    }
  }

  return '';
}

function normalizeMatchValue(value) {
  return String(value ?? '').trim().toUpperCase();
}

function findOptionByAuthValue(options, authValue) {
  const target = normalizeMatchValue(authValue);
  if (!target) return null;
  const optionList = Array.from(options);

  if (target === '4') {
    const englishOption = optionList.find(option =>
      normalizeMatchValue(option.textContent) === 'ENGLISH' ||
      normalizeMatchValue(option.dataset.name) === 'ENGLISH' ||
      normalizeMatchValue(option.dataset.abbreviation) === 'EN'
    );

    if (englishOption) return englishOption;
  }

  const exactValueMatch = optionList.find(option => normalizeMatchValue(option.value) === target);
  if (exactValueMatch) return exactValueMatch;

  const metadataMatch = optionList.find(option => {
    const candidates = [
      option.dataset.id,
      option.dataset.code,
      option.dataset.abbreviation,
      option.dataset.name,
      option.textContent
    ];

    return candidates.some(candidate => normalizeMatchValue(candidate) === target);
  });

  if (metadataMatch) return metadataMatch;

  return null;
}

function applyAuthLanguageDisplay(options, authLanguage) {
  if (normalizeMatchValue(authLanguage) !== '4') return;

  const option = Array.from(options).find(candidate =>
    normalizeMatchValue(candidate.value) === '4' ||
    normalizeMatchValue(candidate.dataset.code) === '4' ||
    normalizeMatchValue(candidate.dataset.id) === '4'
  );

  if (!option) return;

  option.textContent = 'English';
  option.dataset.name = 'English';
  option.dataset.abbreviation = 'EN';
}

function getAuthContextFromToken() {
  const claims = decodeJwtClaims(getAccessToken());

  return {
    country: claims?.country,
    language: claims?.language
  };
}

function getCountryFromHost(hostname) {
  const host = (hostname || '').toLowerCase();

  if (host.endsWith('.be')) return 'BE';
  if (host.endsWith('.nl')) return 'NL';

  return null;
}

function notifyChange() {
  if (typeof onCountryLanguageChange === 'function') {
    onCountryLanguageChange();
  }
}

function syncCustomLanguageSelect(languageSelect) {
  if (!languageSelect) return;

  let shell = document.getElementById('language-select-custom');
  if (!shell) {
    shell = document.createElement('div');
    shell.id = 'language-select-custom';
    shell.className = 'sevenn-custom-select';
    shell.innerHTML = `
      <button type="button" class="sevenn-custom-select-button" aria-haspopup="listbox" aria-expanded="false"></button>
      <div class="sevenn-custom-select-menu" role="listbox"></div>
    `;
    languageSelect.insertAdjacentElement('afterend', shell);
  }

  languageSelect.classList.add('sevenn-native-select-hidden');
  languageSelect.setAttribute('aria-hidden', 'true');
  languageSelect.tabIndex = -1;

  const button = shell.querySelector('.sevenn-custom-select-button');
  const menu = shell.querySelector('.sevenn-custom-select-menu');
  const closeMenu = () => {
    shell.classList.remove('is-open');
    button?.setAttribute('aria-expanded', 'false');
  };

  const render = () => {
    const options = Array.from(languageSelect.options);
    const selectedOption = languageSelect.selectedOptions?.[0] || options[0];
    button.textContent = selectedOption?.textContent?.trim() || 'Language';
    button.disabled = languageSelect.disabled;
    menu.innerHTML = '';

    options.forEach(option => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'sevenn-custom-select-option';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', option.value === languageSelect.value ? 'true' : 'false');
      item.textContent = option.textContent?.trim() || option.value;
      item.disabled = option.disabled;
      item.addEventListener('click', () => {
        if (languageSelect.value !== option.value) {
          languageSelect.value = option.value;
          languageSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
        render();
        closeMenu();
      });
      menu.appendChild(item);
    });
  };

  if (!shell.dataset.bound) {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (languageSelect.disabled) return;
      const open = !shell.classList.contains('is-open');
      document.querySelectorAll('.sevenn-custom-select.is-open').forEach(openShell => {
        if (openShell !== shell) openShell.classList.remove('is-open');
      });
      shell.classList.toggle('is-open', open);
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    document.addEventListener('click', (event) => {
      if (!shell.contains(event.target)) closeMenu();
    });

    shell.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeMenu();
    });

    languageSelect.addEventListener('change', render);
    shell.dataset.bound = 'true';
  }

  render();
}

function normalizeListResponse(payload, preferredKeys = []) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload && typeof payload === 'object') {
    for (const key of preferredKeys) {
      if (Array.isArray(payload[key])) {
        return payload[key];
      }
    }
  }

  return [];
}

/* --------------------------------------------------
   API calls
-------------------------------------------------- */

export async function fetchCountries() {
  const res = await secureFetch(`${API_URL}countries`);
  const payload = await res.json().catch(() => null);
  return normalizeListResponse(payload, ['countries', 'data', 'items']);
}

export async function fetchLanguages(countryCode) {
  const res = await secureFetch(`${API_URL}countries/${countryCode}/languages`);
  const payload = await res.json().catch(() => null);
  return normalizeListResponse(payload, ['languages', 'data', 'items']);
}

/* --------------------------------------------------
   Main setup function
-------------------------------------------------- */

export async function setupCountryLanguageSelectors(map, onChange) {
  const existingState = selectorStateByMap.get(map);
  if (typeof onChange === 'function') {
    onCountryLanguageChange = onChange;
  }

  if (existingState?.initialized) {
    return existingState.promise ?? Promise.resolve();
  }

  if (existingState?.promise) {
    return existingState.promise;
  }

  const setupPromise = (async () => {
    selectorStateByMap.set(map, { promise: null, initialized: false });

    if (!map) {
      console.error("Map not available in country-language");
      return;
    }

    const countrySelect = document.getElementById("country-select");
    const languageSelect = document.getElementById("language-select");

    if (!countrySelect || !languageSelect) {
      console.error("Country or language select not found");
      return;
    }

    if (!countrySelect.dataset.countryLanguageListenerBound) {
      countrySelect.addEventListener('change', notifyChange);
      countrySelect.dataset.countryLanguageListenerBound = 'true';
    }
    if (!languageSelect.dataset.countryLanguageListenerBound) {
      languageSelect.addEventListener('change', notifyChange);
      languageSelect.dataset.countryLanguageListenerBound = 'true';
    }

    const tokenContext = getAuthContextFromToken();
    const mapContext = map.getProperties();
    const jwtContext = {
      ...mapContext,
      country: mapContext?.country || tokenContext.country,
      language: mapContext?.language || tokenContext.language
    };
    const domainCountry = getCountryFromHost(window.location.hostname);

    const jwtCountry = jwtContext?.country || domainCountry;
    const jwtLanguage = jwtContext?.language;
   
    /* -------- Load countries -------- */
    const countries = await fetchCountries();
    countrySelect.innerHTML = "";

    countries.forEach(c => {
      const opt = document.createElement("option");
      const countryValue = getValueByKeys(c, ['id', 'countryId', 'countryID', 'country', 'code']);
      opt.value = String(countryValue);
      opt.textContent = c.name ?? c.displayName ?? countryValue;
      opt.dataset.abbreviation = c.abbreviation;  // ← store ISO code
      opt.dataset.id = String(getValueByKeys(c, ['id', 'countryId', 'countryID']));
      opt.dataset.code = String(getValueByKeys(c, ['code', 'countryCode']));
      opt.dataset.abbreviation = String(getValueByKeys(c, ['abbreviation', 'isoCode', 'shortName']));
      opt.dataset.name = String(c.name ?? c.displayName ?? '');
      countrySelect.appendChild(opt);
    });

    const matchingCountryOption = findOptionByAuthValue(countrySelect.options, jwtCountry);
    countrySelect.value = matchingCountryOption?.value || countrySelect.options[0]?.value || '';

    if (!countrySelect.value && countries.length) {
      countrySelect.value = countrySelect.options[0].value;
    }

    countrySelect.disabled = true;
    countrySelect.setAttribute('aria-readonly', 'true');
    countrySelect.title = 'Country is set by the authentication link';

    /* -------- Load languages -------- */
    async function loadLanguages(countryCode, presetLanguage) {
      if (!countryCode) {
        languageSelect.innerHTML = "";
        return;
      }

      const langs = await fetchLanguages(countryCode);
      languageSelect.innerHTML = "";

      langs.forEach(l => {
        const opt = document.createElement("option");
        const languageValue = getValueByKeys(l, ['code', 'languageCode', 'id', 'languageId', 'languageID', 'language']);
        opt.value = String(languageValue);
        opt.textContent = l.name ?? l.displayName ?? languageValue;
        opt.dataset.id = String(getValueByKeys(l, ['id', 'languageId', 'languageID']));
        opt.dataset.code = String(getValueByKeys(l, ['code', 'languageCode']));
        opt.dataset.abbreviation = String(getValueByKeys(l, ['abbreviation', 'isoCode', 'shortName']));
        opt.dataset.name = String(l.name ?? l.displayName ?? '');
        languageSelect.appendChild(opt);
      });

      if (!languageSelect.options.length) {
        return;
      }

      applyAuthLanguageDisplay(languageSelect.options, presetLanguage);

      if (presetLanguage) {
        const match = findOptionByAuthValue(languageSelect.options, presetLanguage);
        if (match) {
          languageSelect.value = match.value;
        } else {
          console.warn('Auth language was not found in available languages', {
            authLanguage: presetLanguage,
            availableLanguages: Array.from(languageSelect.options).map(option => ({
              value: option.value,
              code: option.dataset.code,
              id: option.dataset.id,
              name: option.textContent
            }))
          });
          languageSelect.value = languageSelect.options[0].value;
        }
      } else {
        languageSelect.value = languageSelect.options[0].value;
      }
      syncCustomLanguageSelect(languageSelect);
    }

    await loadLanguages(countrySelect.value, jwtLanguage);
    const authLanguageOption = findOptionByAuthValue(languageSelect.options, jwtLanguage);
    if (authLanguageOption) {
      languageSelect.value = authLanguageOption.value;
    }
    syncCustomLanguageSelect(languageSelect);

    languageSelect.disabled = false;
    languageSelect.removeAttribute('aria-readonly');
    languageSelect.removeAttribute('title');
    syncCustomLanguageSelect(languageSelect);

    /* -------- Initial JWT-driven load -------- */
    map.set("country", countrySelect.value);
    map.set("language", languageSelect.value);

    await loadLanguageTranslations();
    await loadColumns();
    await loadDynamicBaseLayers(map);

    /* -------- User-driven changes -------- */
    if (!languageSelect.dataset.languageChangeBound) {
      languageSelect.addEventListener("change", async () => {
        await reloadAfterSelection(countrySelect, languageSelect, map);
      });
      languageSelect.dataset.languageChangeBound = 'true';
    }

    if (!countrySelect.dataset.countryChangeBound) {
      countrySelect.addEventListener("change", async () => {
        await loadLanguages(countrySelect.value, jwtLanguage);
        languageSelect.disabled = false;
        syncCustomLanguageSelect(languageSelect);
        await reloadAfterSelection(countrySelect, languageSelect, map);
      });
      countrySelect.dataset.countryChangeBound = 'true';
    }

    selectorStateByMap.set(map, { promise: setupPromise, initialized: true });
  })();

  selectorStateByMap.set(map, { promise: setupPromise, initialized: false });
  return setupPromise;
}

/* --------------------------------------------------
   Reload helper
-------------------------------------------------- */

async function reloadAfterSelection(countrySelect, languageSelect, map) {
  const country = countrySelect.value;
  const language = languageSelect.value;

  if (!country || !language) return;

  map.set("country", country);
  map.set("language", language);

  await loadLanguageTranslations();
  await loadColumns();
  await loadDynamicBaseLayers(map);
}

/* --------------------------------------------------
   Utility getters
-------------------------------------------------- */

export function getSelectedCountryCode() {
  const countrySelect = document.getElementById('country-select');
  return countrySelect ? countrySelect.value : null;
}

export function getSelectedLanguageCode() {
  const languageSelect = document.getElementById('language-select');
  return languageSelect ? languageSelect.value : null;
}

export function getSelectedCountryAbbreviation() {
  const select = document.getElementById('country-select');
  const option = select?.selectedOptions?.[0];
  return option?.dataset?.abbreviation?.toLowerCase() || null;
}
